import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import { Close, ShoppingCart, Visibility } from '@mui/icons-material'
import type { Pacote } from '../../types'
import { apiService } from '../../services/api'
import { useCart } from '../../contexts/CartContext'

const brl = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const PackagesSection: React.FC = () => {
  const { addItem, getItemQuantity } = useCart()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pacotes, setPacotes] = useState<Pacote[] | null>(null)
  const [selected, setSelected] = useState<Pacote | null>(null)

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.listarPacotes()
        if (!mounted) return
        setPacotes(Array.isArray(data) ? data : [])
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao carregar pacotes.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [])

  const items = useMemo<Array<Pacote | null>>(() => (pacotes ? pacotes : [null, null, null]), [pacotes])

  const closeDetails = () => setSelected(null)

  return (
    <Box id="pacotes" sx={{ py: { xs: 7, md: 10 } }}>
      <Container maxWidth="lg">
        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 900 }}>
            Pacotes para Palworld
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          {items.map((p, idx) => (
            <Grid key={p?.id ?? idx} item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.10)',
                  background:
                    'radial-gradient(700px 260px at 10% 0%, rgba(0,217,255,0.10), transparent 60%),' +
                    'radial-gradient(700px 260px at 90% 30%, rgba(255,109,0,0.08), transparent 60%)'
                }}
                onClick={() => p && setSelected(p)}
              >
                {!p ? (
                  <Skeleton variant="rectangular" height={240} />
                ) : p.urlImagem ? (
                  <Box
                    sx={{
                      height: 260,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      borderBottom: '1px solid rgba(255,255,255,0.10)',
                      bgcolor: 'rgba(255,255,255,0.02)'
                    }}
                  >
                    <Box
                      component="img"
                      src={p.urlImagem}
                      alt={`Imagem do pacote ${p.nome}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 260,
                      borderBottom: '1px solid rgba(255,255,255,0.10)',
                      background:
                        'linear-gradient(135deg, rgba(0,230,118,0.18), rgba(0,217,255,0.14), rgba(255,109,0,0.12))'
                    }}
                  />
                )}

                <CardContent sx={{ p: 3 }}>
                  {!p ? (
                    <>
                      <Skeleton variant="text" height={44} width="55%" />
                      <Skeleton variant="text" height={28} width="75%" />
                      <Skeleton variant="rectangular" height={44} sx={{ mt: 2, borderRadius: 2 }} />
                    </>
                  ) : (
                    <Stack spacing={2.25}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                          {p.nome}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                          {p.vip && <Chip size="small" label={`VIP ${p.diasVip ?? 0}d`} color="info" />}
                          {p.warzoneCoin && <Chip size="small" label="Warzone Coin" color="warning" />}
                          <Chip size="small" label={`${p.pontos ?? 0} pontos`} />
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          p: 2.25,
                          borderRadius: 3,
                          border: '1px solid rgba(255,255,255,0.10)',
                          background: 'rgba(255,255,255,0.03)'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          valor
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                          {brl(Number(p.preco) || 0)}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                        {p.descricao || '—'}
                      </Typography>

                      {p.vip && p.descricaoVip && (
                        <Alert severity="info" sx={{ py: 1 }}>
                          <strong>VIP:</strong> {p.descricaoVip}
                        </Alert>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelected(p)
                          }}
                          fullWidth
                        >
                          Ver detalhes
                        </Button>
                        <Button
                          variant="contained"
                          color="info"
                          startIcon={<ShoppingCart />}
                          onClick={(e) => {
                            e.stopPropagation()
                            addItem(p)
                          }}
                          fullWidth
                          disabled={loading}
                        >
                          {getItemQuantity(p.id) > 0 ? `Adicionar (${getItemQuantity(p.id)})` : 'Adicionar'}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Dialog
        open={!!selected}
        onClose={closeDetails}
        fullWidth
        maxWidth="md"
        BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.78)' } }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(12, 13, 24, 0.96)',
            border: '1px solid rgba(255,255,255,0.10)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                  {selected.nome}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  {selected.vip && <Chip size="small" label={`VIP ${selected.diasVip ?? 0}d`} color="info" />}
                  {selected.warzoneCoin && <Chip size="small" label="Warzone Coin" color="warning" />}
                  <Chip size="small" label={`${selected.pontos ?? 0} pontos`} />
                </Stack>
              </Box>
              <IconButton onClick={closeDetails} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Stack spacing={2}>
                {selected.urlImagem && (
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: 240, md: 360 },
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.10)',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 1.5, md: 2 },
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={selected.urlImagem}
                      alt={`Imagem do pacote ${selected.nome}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                )}
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {brl(Number(selected.preco) || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.descricao || '—'}
                  </Typography>
                </Box>

                {selected.vip && selected.descricaoVip && (
                  <Alert severity="info">
                    <strong>VIP:</strong> {selected.descricaoVip}
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button onClick={closeDetails} variant="outlined">
                Fechar
              </Button>
              <Button
                variant="contained"
                startIcon={<ShoppingCart />}
                onClick={() => {
                  addItem(selected)
                  closeDetails()
                }}
              >
                Adicionar ao carrinho
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default PackagesSection
